/**
 * Web App Config Service
 *
 * Resolution order (first source of truth wins):
 * 1. URL params (?view=, ?section=) – for view and section
 * 2. Config from API (config.defaultView, config.routes, config.sections, etc.)
 * 3. Hardcoded defaults (HARDCODED_DEFAULT_VIEW, DEFAULT_ROUTES, DEFAULT_CONFIG)
 *
 * Fetches config from API. Falls back to built-in defaults when API is unavailable.
 */

export type ViewType = 'main' | 'privacy' | 'download' | 'demo' | 'home' | 'events' | 'event' | 'booked' | 'childSafety';

/** Routes configurable via CRM – controls valid view/section URL params */
export interface RoutesConfig {
  /** Valid view param values (e.g. main, privacy, download, demo, home, careers) */
  views: string[];
  /** Valid section param values for view=main, in display order (index = section index) */
  sections: string[];
}

export interface WebAppConfig {
  /** Routes for URL params – CRM configurable. Falls back to defaults when absent. */
  routes?: RoutesConfig;
  defaultView: ViewType;
  sections: {
    showHome: boolean;
    showDownload: boolean;
    showBookDemo: boolean;
    showPrivacyPolicy: boolean;
    showExperience: boolean;
    showVenues: boolean;
    showCommunity: boolean;
    showTechnology: boolean;
    showCareers: boolean;
  };
  hero: {
    leftTitle: string;
    leftSubtitle: string;
    tagline1: string;
    tagline2: string;
  };
  venues: {
    headline: string;
    subheadline: string;
    ctaText: string;
    features?: { title: string; description: string; icon?: string }[];
    stats?: { value: string; label: string }[];
    testimonialQuote?: string;
    testimonialAuthor?: string;
  };
  bookDemo: {
    headline: string;
    subheadline: string;
  };
  ctas: {
    download: { text: string; visible: boolean };
    bookDemo: { text: string; visible: boolean };
  };
  download: {
    title: string;
    subtitle: string;
    iosUrl: string;
    androidUrl: string;
  };
  shell: {
    versionText: string;
    supportEmail: string;
    splashDurationMs: number;
  };
}

/** Hardcoded fallback when config is unavailable and no URL param is present. */
export const HARDCODED_DEFAULT_VIEW: ViewType = 'home';

/** Default routes – used when config.routes is absent or partial. */
export const DEFAULT_ROUTES: RoutesConfig = {
  views: ['main', 'privacy', 'download', 'demo', 'home', 'careers', 'events', 'event', 'booked', 'childSafety'],
  sections: ['experience', 'venues', 'community', 'technology', 'careers'],
};

/** Views that are always available regardless of CRM config */
const ALWAYS_AVAILABLE_VIEWS = ['events', 'event', 'booked', 'childSafety'];

/** Get effective routes from config, merging with defaults. */
export function getRoutes(config: WebAppConfig): RoutesConfig {
  const r = config.routes;
  if (!r || (Array.isArray(r.views) && r.views.length === 0)) return DEFAULT_ROUTES;
  const views = Array.isArray(r.views) && r.views.length > 0 ? r.views : DEFAULT_ROUTES.views;
  const merged = Array.from(new Set(views.concat(ALWAYS_AVAILABLE_VIEWS)));
  return {
    views: merged,
    sections: Array.isArray(r.sections) && r.sections.length > 0 ? r.sections : DEFAULT_ROUTES.sections,
  };
}

/** Built-in defaults matching current app behavior. Used when API is unavailable. */
export const DEFAULT_CONFIG: WebAppConfig = {
  routes: DEFAULT_ROUTES,
  defaultView: HARDCODED_DEFAULT_VIEW,
  sections: {
    showHome: true,
    showDownload: true,
    showBookDemo: true,
    showPrivacyPolicy: true,
    showExperience: true,
    showVenues: true,
    showCommunity: true,
    showTechnology: true,
    showCareers: true,
  },
  hero: {
    leftTitle: 'Collected',
    leftSubtitle: 'AF',
    tagline1: 'Every person is an opportunity — and the world is yours to collect.',
    tagline2: 'The first proximity-powered platform that connects people and venues in real time.',
  },
  venues: {
    headline: 'Your Event. Your Audience. Live.',
    subheadline: 'See every customer. Reach them instantly. From one dashboard.',
    ctaText: 'Request a Demo — We\'ll Build Your Event Page Live',
  },
  bookDemo: {
    headline: 'Request a Demo',
    subheadline: 'See how CollectedAF transforms your venue into an interactive, real-time experience',
  },
  ctas: {
    download: { text: 'Download App', visible: true },
    bookDemo: { text: 'Book a Demo', visible: true },
  },
  download: {
    title: 'Download CollectedAF',
    subtitle: 'Scan the QR code or click the button to download',
    iosUrl: 'https://apps.apple.com/us/app/collectedaf/id1592301226',
    androidUrl: 'https://play.google.com/store/apps/details?hl=en_US&id=com.collectedAF.collectedAF',
  },
  shell: {
    versionText: 'VERSION 9.6.6',
    supportEmail: 'support@collectedaf.com',
    splashDurationMs: 6000,
  },
};

function deepMerge<T extends object>(target: T, source: Partial<T>): T {
  const result = { ...target };
  for (const key of Object.keys(source) as (keyof T)[]) {
    const val = source[key];
    if (val != null && typeof val === 'object' && !Array.isArray(val)) {
      (result as Record<string, unknown>)[key as string] = deepMerge(
        (target[key] as object) || {},
        val as Record<string, unknown>
      );
    } else if (val !== undefined) {
      (result as Record<string, unknown>)[key as string] = val;
    }
  }
  return result;
}

/** Fetch config from API. Returns defaults on any failure. */
export async function fetchWebAppConfig(): Promise<WebAppConfig> {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (!apiUrl || apiUrl.trim() === '') {
    return DEFAULT_CONFIG;
  }

  try {
    const res = await fetch(`${apiUrl.replace(/\/$/, '')}/api/v1/web-app/config`);
    if (!res.ok) {
      return DEFAULT_CONFIG;
    }
    const body = await res.json();
    const config = body?.config ?? body; // API returns { config: {...} } or top-level config
    if (!config || typeof config !== 'object') {
      return DEFAULT_CONFIG;
    }
    return deepMerge(DEFAULT_CONFIG, config) as WebAppConfig;
  } catch {
    return DEFAULT_CONFIG;
  }
}
